/**
 * Wallet Geo Restriction Modal
 *
 * Shown instead of the wallet selection list when the user is in a
 * geo-restricted location. Bottom-drawer on mobile, centered on desktop.
 */

import {
    ArrowRightLongIcon,
    CrossIcon
} from "@solomon-labs/design/components/icons";
import {
    Button,
    ButtonIcon,
    Dialog,
    DialogClose,
    DialogContent,
    DialogTitle
} from "@solomon-labs/design/components/ui";
import React from "react";

interface WalletGeoRestrictionModalProps {
    ip: string | undefined;
    country: string | undefined;
    onClose: (reason?: string) => void;
}

export function WalletGeoRestrictionModal({
    ip,
    country,
    onClose
}: WalletGeoRestrictionModalProps): React.ReactElement {
    return (
        <Dialog
            open
            onOpenChange={(v): void => {
                if (!v) onClose();
            }}
        >
            <DialogContent
                className="w-full max-w-none md:max-w-[384px] max-h-[80vh] overflow-y-auto top-auto bottom-0 translate-y-0 md:top-1/2 md:bottom-auto md:-translate-y-1/2 flex flex-col gap-3 pt-4 pb-0"
                aria-label="Location Restricted"
            >
                <div className="flex items-center justify-between px-4">
                    <div className="flex items-start gap-1">
                        <div className="pt-[7px]">
                            <div className="w-1 h-1 bg-accent" />
                        </div>
                        <DialogTitle>Location Restricted</DialogTitle>
                    </div>
                    <DialogClose
                        onClick={(): void => onClose("close_button")}
                        render={
                            <ButtonIcon
                                variant="transparent"
                                size="xl"
                                aria-label="Close"
                                icon={<CrossIcon />}
                            />
                        }
                    />
                </div>

                <div className="px-4">
                    <div className="h-px bg-line-base" />
                </div>

                <div className="flex flex-col items-center gap-4 px-4 py-4">
                    {ip && (
                        <span className="font-numeric text-3xl tracking-[-0.7px] text-foreground-base tabular-nums">
                            {ip}
                        </span>
                    )}
                    {country && (
                        <span className="font-sans text-base text-foreground-neutral">
                            {country}
                        </span>
                    )}
                    <p className="font-sans text-sm text-foreground-neutral text-center">
                        This IP address location is restricted. Wallet
                        connection is not available in your region.
                    </p>
                </div>

                <div className="px-4">
                    <Button
                        variant="raised"
                        size="lg"
                        interaction="lifted"
                        fullWidth
                        onClick={(): void => onClose("browse_only")}
                        trailing={<ArrowRightLongIcon className="w-6 h-6" />}
                    >
                        Browse Only
                    </Button>
                </div>

                <div className="h-safe-area-inset-bottom" />
            </DialogContent>
        </Dialog>
    );
}
